import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { setLoginSession } from '../Utils/CommonUtil';
import { getUserInfo } from '../Services/LoginService';
import { Card, CardBody } from '@progress/kendo-react-layout';
import { Loader2 } from 'lucide-react';
import './Login.scss';

const V2_AUTH_STAGE = 'v2';
const V2_LOGIN_IN_PROGRESS_KEY = 'smartapps.v2-login-in-progress';

export default function LoginCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updateUser, updateTenant, logoutLocalSession, authLoader } =
    useAuth();

  // Use ref to track if auth has already been initiated
  const authInitiatedRef = React.useRef(false);

  useEffect(() => {
    // Prevent multiple executions - only run once
    if (authInitiatedRef.current) {
      return;
    }
    authInitiatedRef.current = true;

    const handleAuth = async () => {
      const isV2Configured = Boolean(
        process.env.OIDC_LOGIN_URL_V2 && process.env.BASE_URL_V2,
      );
      const callbackStage = searchParams.get('authStage');
      const isV2Return = callbackStage === V2_AUTH_STAGE;
      const hasPendingV2Login =
        typeof window !== 'undefined' &&
        window.sessionStorage?.getItem(V2_LOGIN_IN_PROGRESS_KEY) === '1';

      try {
        if (isV2Configured && !isV2Return && !hasPendingV2Login) {
          const v1UserInfoResponse = await getUserInfo();
          if (!v1UserInfoResponse?.IsSuccess || !v1UserInfoResponse?.Payload) {
            navigate('/login?error=auth-failed');
            return;
          }

          window.sessionStorage?.setItem(V2_LOGIN_IN_PROGRESS_KEY, '1');
          window.location.replace(process.env.OIDC_LOGIN_URL_V2);
          return;
        }

        if (isV2Configured && (isV2Return || hasPendingV2Login)) {
          console.log(
            'V2 silent login callback response:',
            Object.fromEntries(searchParams.entries()),
          );
          window.sessionStorage?.removeItem(V2_LOGIN_IN_PROGRESS_KEY);
        }

        const userInfoResponse = await getUserInfo();
        if (!userInfoResponse?.IsSuccess || !userInfoResponse?.Payload) {
          navigate('/login?error=auth-failed');
          return;
        }

        try {
          await setLoginSession({
            userData: userInfoResponse.Payload,
            updateUser,
            updateTenant,
            page: 'login-callback',
          });
          // window.location.replace(INDEX_PAGE);
        } catch (err) {
          window.sessionStorage?.removeItem(V2_LOGIN_IN_PROGRESS_KEY);
          await logoutLocalSession({ shouldRedirect: false });
          navigate('/login?error=auth-failed');
        }
      } catch (err) {
        window.sessionStorage?.removeItem(V2_LOGIN_IN_PROGRESS_KEY);
        await logoutLocalSession({ shouldRedirect: false });
        console.error('Login callback failed:', err);
        navigate('/login?error=auth-failed');
      }
    };

    handleAuth();
  }, []);

  return (
    <div className='sso-login'>
      <div className='sso-login__inner'>
        <Card className='sso-login__card'>
          <CardBody className='sso-login__cardContent sso-login__loading'>
            <Loader2 className='sso-login__pageSpinner' />
            <h1 className='sso-login__loadingTitle'>Signing you in</h1>
            <p className='sso-login__loadingSub'>Completing authentication…</p>
            {authLoader ? (
              <p className='sso-login__loadingSub'>Please wait…</p>
            ) : (
              <p className='sso-login__loadingSub'>Redirecting…</p>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
